import { useQueryClient } from "@tanstack/react-query";
import { Button, Card, Tag, Typography } from "antd";
import dayjs from "dayjs";
import { Clock, FileSignature, User, ChevronRight, ChevronDown, MapPin, Users2, Package, Wrench, Box } from "lucide-react";
import { FC, useState } from "react";
import { useNotification } from "~/hooks";
import {
  SERVICE_TICKET_KEYS,
  useUpdateServiceTicket,
} from "~/services/service-ticket";
import { useAuth } from "~/store";
import { TServiceTicket } from "~/types";
import {
  capitalize,
  formatDimensions,
  formatFullAddress,
  formatPackingType,
  getClientName,
} from "~/utils";
import { JobStatus } from "~/utils/constants";
import ServiceDetailsModal from "./service-details-modal";
import { StatusBadge } from "./status-badge";
import { useCompleteTicket } from "~/components/common/complete-ticket/complete-ticket.store";
import { ServiceCompletionDetails } from "~/components/common/service-completion-details";

const { Title, Text } = Typography;

// Utility function to get correct datetime by combining serviceDatetime date with startDatetime time
const getCorrectDateTime = (ticket: any) => {
  if (!ticket.serviceDatetime || !ticket.startDatetime) {
    return ticket.serviceDatetime || ticket.startDatetime;
  }

  const serviceDate = dayjs(ticket.serviceDatetime);
  const startTime = dayjs(ticket.startDatetime);

  // Combine: date from serviceDatetime + time from startDatetime
  return serviceDate
    .hour(startTime.hour())
    .minute(startTime.minute())
    .second(startTime.second())
    .millisecond(startTime.millisecond())
    .toDate();
};

type Props = {
  ticket: TServiceTicket;
  variant?: 'compact' | 'detailed';
  initialExpanded?: boolean;
};

export const AssignmentTicket: FC<Props> = ({ ticket, variant = 'detailed', initialExpanded = false }) => {
  const qc = useQueryClient();
  const { user } = useAuth();
  const { success, error } = useNotification();
  const [ticketId, setTicketId] = useState<string | null>(null);
  const [detailsVisible, setDetailsVisible] = useState(false);
  const [expanded, setExpanded] = useState(initialExpanded);
  const { openCompleteTicket } = useCompleteTicket();

  const updateMutation = useUpdateServiceTicket();

  const getCompletionStatusIndicators = (ticket: TServiceTicket) => {
    const indicators = [];

    if (ticket.timeIn && ticket.timeOut) {
      indicators.push(
        <div
          key="time"
          className="flex items-center space-x-1 text-xs text-green-600"
        >
          <Clock size={10} />
          <span>Time</span>
        </div>
      );
    }

    if (ticket.clientSignature) {
      indicators.push(
        <div
          key="signature"
          className="flex items-center space-x-1 text-xs text-green-600"
        >
          <FileSignature size={10} />
          <span>Signed</span>
        </div>
      );
    }

    if (ticket.staffCheckoff) {
      indicators.push(
        <div
          key="checkoff"
          className="flex items-center space-x-1 text-xs text-green-600"
        >
          <User size={10} />
          <span>Confirmed</span>
        </div>
      );
    }

    return indicators;
  };

  const onStartService = async (ticketId: string) => {
    try {
      // Round current time to nearest 15 minutes and convert to proper UTC
      const now = dayjs();
      const minutes = now.minute();
      const roundedMinutes = Math.round(minutes / 15) * 15;
      const roundedTime = now.minute(roundedMinutes).second(0).millisecond(0);
      const timeInISO = roundedTime.toISOString();

      await updateMutation.mutateAsync({
        id: ticketId,
        status: JobStatus.IN_PROGRESS,
        timeIn: timeInISO,
      });
      qc.invalidateQueries({
        queryKey: SERVICE_TICKET_KEYS.GET_MY_SERVICE_TICKETS({}),
      });
      success({ message: "Service started successfully" });
    } catch (err) {
      console.error("Error updating service ticket:", err);
      error({ message: "Failed to start service" });
    }
  };

  const onCompleteService = () => {
    // Open the complete ticket modal (same as admin side)
    openCompleteTicket(ticket);
  };

  const onCancelService = async (ticketId: string) => {
    try {
      await updateMutation.mutateAsync({
        id: ticketId,
        status: JobStatus.SCHEDULED,
        timeIn: null,
        timeOut: null,
      });
      qc.invalidateQueries({
        queryKey: SERVICE_TICKET_KEYS.GET_MY_SERVICE_TICKETS({}),
      });
      success({ message: "Service cancelled, ticket moved back to open" });
    } catch (err) {
      console.error("Error cancelling service ticket:", err);
      error({ message: "Failed to cancel service" });
    }
  };

  // Get booking client name (main client from jobTicket)
  const getBookingClientName = () => {
    return ticket.jobTicket?.client?.companyName || "Unknown Booking Client";
  };

  // Get service client name (based on service type)
  const getServiceClientName = () => {
    return ticket.clientService?.companyName || ticket.client?.companyName || getClientName(ticket.client);
  };

  // Get service reference number (use serial field)
  const getServiceReference = () => {
    return ticket.serial || `#${ticket.id.slice(-6)}`;
  };

  // Format time to ensure proper 12-hour format
  const formatTime = (datetime: string | Date) => {
    if (!datetime) return "";
    const date = new Date(datetime);
    return date.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
  };

  // Get contact information
  const getContactInfo = () => {
    const contactName = ticket.clientService?.contactName || ticket.client?.contactName || 
                       `${ticket.client?.firstName || ''} ${ticket.client?.lastName || ''}`.trim();
    const contactPhone = ticket.clientService?.contactPhone || ticket.client?.contactPhone;
    
    return {
      name: contactName || "No contact",
      phone: contactPhone || "No phone"
    };
  };

  // Get team members (all assigned staff)
  const getTeamMembers = () => {
    console.log('=== GET TEAM MEMBERS DEBUG ===');
    console.log('Full ticket object:', ticket);
    console.log('ticket.staffs:', ticket.staffs);
    console.log('ticket.staff:', ticket.staff);
    console.log('All ticket keys:', Object.keys(ticket));
    console.log('ticket.staffs length:', ticket.staffs?.length);

    if (!ticket.staffs || ticket.staffs.length === 0) {
      return "No team members assigned";
    }

    const staffNames = ticket.staffs.map((staff, index) => {
      console.log(`Staff ${index}:`, staff);
      console.log(`  - lead:`, staff.lead);
      console.log(`  - driver:`, staff.driver);
      console.log(`  - user:`, staff.user);

      // Try multiple ways to get the staff name
      let name = "Unknown Staff";

      if (staff.user) {
        if (staff.user.firstName && staff.user.lastName) {
          name = `${staff.user.firstName} ${staff.user.lastName}`.trim();
        } else if (staff.user.username) {
          name = staff.user.username;
        } else {
          name = getClientName(staff.user);
        }
      }

      console.log(`  - resolved name:`, name);

      // Add role indicators
      const roles = [];
      if (staff.lead) roles.push("L");
      if (staff.driver) roles.push("D");

      console.log(`  - roles:`, roles);

      // Build the display name
      let displayName = name;
      if (roles.length > 0) {
        displayName += ` (${roles.join(", ")})`;
      }

      console.log(`  - final display name:`, displayName);

      return displayName;
    });

    console.log('All staff names:', staffNames);
    const result = staffNames.join(", ");
    console.log('Final team members string:', result);

    return result;
  };

  // Show only first 5 inventory items with rich details
  const getDisplayInventories = () => {
    const inventories = ticket.inventories || [];
    return {
      display: inventories.slice(0, 5),
      hasMore: inventories.length > 5,
      total: inventories.length
    };
  };

  // Format inventory item with full details
  const formatInventoryItem = (item: any) => {
    const parts = [];

    // Reference numbers
    if (item.inventoryRef) parts.push(item.inventoryRef);
    if (item.clientInternalRef) parts.push(`(${item.clientInternalRef})`);

    // Title and artist
    let titleLine = item.title || "Untitled";
    if (item.artistName) titleLine += ` by ${item.artistName}`;

    return {
      refLine: parts.join(" "),
      titleLine,
      packingType: formatPackingType(item),
      dimensions: formatDimensions(item),
      value: item.value ? `${item.value.toLocaleString()}` : ""
    };
  };

  if (variant === 'compact') {
    return (
      <>
      <div
        className={`border rounded-lg p-4 transition-colors ${
          ticket.status === JobStatus.IN_PROGRESS ? "border-blue-300 bg-blue-50" : "border-gray-200"
        }`}
      >
        <div className="flex justify-between items-center">
          {/* Left side: Service info */}
          <div className="flex items-center space-x-3">
            <button
              className="p-1 rounded hover:bg-gray-100"
              onClick={(e) => {
                e.stopPropagation();
                setExpanded(!expanded);
              }}
            >
              {expanded ? (
                <ChevronDown size={16} className="text-gray-600" />
              ) : (
                <ChevronRight size={16} className="text-gray-600" />
              )}
            </button>

            <div>
              {/* Service type and reference number - big and bold */}
              <div className="font-bold text-lg flex items-center gap-2">
                <span className="text-blue-600">{capitalize(ticket.serviceType)}</span>
                <span className="text-gray-400">|</span>
                <span>{getServiceReference()}</span>
              </div>

              {/* Service client name - bold and bigger */}
              <div className="font-bold text-base text-black mt-1">
                {getServiceClientName()}
              </div>

              {/* Location address under service client */}
              {ticket.location && (
                <div className="font-bold text-base text-black mt-0.5 whitespace-pre-line">
                  {formatFullAddress(ticket.location)}
                </div>
              )}

              {/* Start time and duration */}
              <div className="flex items-center mt-1 space-x-3 text-sm">
                <div className="text-gray-600">
                  {formatTime(getCorrectDateTime(ticket))}
                </div>
                <div className="text-gray-500">
                  {ticket.duration}H Est. Duration
                </div>
              </div>
            </div>
          </div>

          {/* Right side: Status and summary */}
          <div className="text-right">
            <StatusBadge status={ticket.status} />
            <div className="text-xs text-gray-500 mt-1">
              {ticket.location?.name}
            </div>
            <div className="text-xs text-gray-500">
              {ticket.inventories?.length || 0} items
            </div>
            <div className="flex space-x-1 mt-1">
              {getCompletionStatusIndicators(ticket)}
            </div>
          </div>
        </div>

        {/* Expanded content */}
        {expanded && (
          <div className="mt-4 pt-4 border-t bg-gray-50 -mx-4 px-4 rounded-b-lg">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="space-y-3">
                <div className="flex justify-between items-start">
                  <div>
                    <div className="flex items-center space-x-1 text-base text-black font-medium mb-1">
                      <span>🏢</span>
                      <span>Service Client</span>
                    </div>
                    <Text className="text-sm font-medium">
                      {getServiceClientName()}
                    </Text>
                  </div>
                  <div className="text-right">
                    <div className="text-base text-black font-medium mb-1">
                      Booking Client
                    </div>
                    <Text className="text-sm font-medium">
                      {getBookingClientName()}
                    </Text>
                  </div>
                </div>

                <div>
                  <div className="flex items-center space-x-1 text-base text-black font-medium mb-1">
                    <MapPin size={12} />
                    <span>Location</span>
                  </div>
                  <Text className="text-sm block font-medium text-black">
                    {ticket.location?.name}
                  </Text>
                  <div className="text-sm text-black whitespace-pre-line">
                    {formatFullAddress(ticket.location)}
                  </div>
                  <div className="text-sm text-black mt-1">
                    <span className="font-medium">Contact:</span> {getContactInfo().name}
                    {getContactInfo().phone && ` • ${getContactInfo().phone}`}
                  </div>
                </div>

                <div>
                  <div className="flex items-center space-x-1 text-base text-black font-medium mb-1">
                    <Users2 size={12} />
                    <span>Team</span>
                  </div>
                  <Text className="text-sm">
                    {getTeamMembers()}
                  </Text>
                </div>

                {(ticket.scope || ticket.staffNotes) && (
                  <div>
                    <div className="flex items-center space-x-1 text-base text-black font-medium mb-1">
                      <span>📝</span>
                      <span>Notes</span>
                    </div>
                    {ticket.scope && (
                      <div className="text-sm mb-1">
                        <span className="font-medium">Scope:</span> {ticket.scope}
                      </div>
                    )}
                    {ticket.staffNotes && (
                      <div className="text-sm">
                        <span className="font-medium">Staff Notes:</span> {ticket.staffNotes}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="space-y-3">
                {getDisplayInventories().total > 0 && (
                  <div>
                    <div className="flex items-center space-x-1 text-base text-black font-medium mb-1">
                      <Package size={12} />
                      <span>Inventory ({getDisplayInventories().total} items)</span>
                    </div>
                    <div className="space-y-2">
                      {getDisplayInventories().display.map((item) => {
                        const formatted = formatInventoryItem(item);
                        return (
                          <div key={item.id} className="text-xs border-l-2 border-gray-200 pl-2">
                            <div className="font-medium">{formatted.refLine}</div>
                            <div className="text-gray-600">{formatted.titleLine}</div>
                            <div className="flex justify-between text-gray-500 mt-1">
                              <span>{formatted.packingType}</span>
                              <span>{formatted.dimensions}</span>
                            </div>
                            {formatted.value && (
                              <div className="text-green-600 font-medium">{formatted.value}</div>
                            )}
                          </div>
                        );
                      })}
                      {getDisplayInventories().hasMore && (
                        <div className="text-xs text-blue-600">
                          +{getDisplayInventories().total - 5} more items
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {ticket.supplyItems && ticket.supplyItems.length > 0 && (
                  <div>
                    <div className="flex items-center space-x-1 text-base text-black font-medium mb-1">
                      <Box size={12} />
                      <span>Supplies ({ticket.supplyItems.length} items)</span>
                    </div>
                    <div className="space-y-1">
                      {ticket.supplyItems.map((item: any) => (
                        <div key={item.id} className="text-xs border-l-2 border-blue-200 pl-2">
                          <div className="flex justify-between">
                            <span className="font-medium">{item.supply?.name || 'Unknown Supply'}</span>
                            <span className="text-gray-600">Qty: {item.quantity}</span>
                          </div>
                          {item.supply?.supplyRef && (
                            <div className="text-gray-500 text-[10px]">{item.supply.supplyRef}</div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {ticket.addOnServices && ticket.addOnServices.length > 0 && (
                  <div>
                    <div className="flex items-center space-x-1 text-xs text-gray-500 mb-1">
                      <Wrench size={12} />
                      <span>Added Services</span>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {ticket.addOnServices.map((service, index) => (
                        <Tag key={index} className="text-xs">
                          {service.name}
                        </Tag>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Service Completion Details */}
            {ticket.status === JobStatus.COMPLETED && ticket.completedAt && (
              <ServiceCompletionDetails serviceTicket={ticket} showTitle={false} />
            )}

            <div className="mt-4 pt-3 border-t flex justify-end gap-2">
              <Button
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setDetailsVisible(true);
                  setTicketId(ticket.id);
                }}
              >
                View Details
              </Button>
              {ticket.status === JobStatus.SCHEDULED && (
                <Button
                  type="primary"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onStartService(ticket.id);
                  }}
                  loading={updateMutation.isPending}
                >
                  Start Service
                </Button>
              )}
              {ticket.status === JobStatus.IN_PROGRESS && (
                <>
                  <Button
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      onCancelService(ticket.id);
                    }}
                    loading={updateMutation.isPending}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="primary"
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      onCompleteService();
                    }}
                    loading={updateMutation.isPending}
                  >
                    Complete Service
                  </Button>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      <ServiceDetailsModal
        visible={detailsVisible}
        ticketId={ticketId}
        onClose={() => {
          setDetailsVisible(false);
          setTicketId(null);
        }}
      />
      </>
    );
  }

  // Detailed variant (original layout with improvements)
  return (
    <>
      <Card
        key={ticket.id}
        className={`border-0 md:border shadow-sm md:shadow-md hover:shadow-lg transition-shadow ${
          ticket.status === JobStatus.IN_PROGRESS ? "border-blue-300" : ""
        }`}
        classNames={{ body: "p-0" }}
      >
        <div
          className={`p-3 md:p-4 rounded-t-lg ${
            ticket.status === JobStatus.IN_PROGRESS ? "bg-blue-50" : "bg-white"
          }`}
        >
          <div className="flex flex-col md:flex-row justify-between items-start gap-3 md:gap-4">
            <div className="flex-1 min-w-0">
              {/* Service reference number */}
              <div className="flex items-center space-x-2 mb-1">
                <Text strong className="text-xs text-gray-500">
                  {getServiceReference()}
                </Text>
                <Tag size="small">{capitalize(ticket.serviceType)}</Tag>
              </div>

              {/* Time and duration */}
              <Text strong className="text-sm md:text-base block">
                {formatTime(getCorrectDateTime(ticket))} -{" "}
                {formatTime(dayjs(getCorrectDateTime(ticket)).add(ticket.duration ?? 0, "hour").toDate())}
                <span className="text-sm text-gray-500 ml-2">
                  ({ticket.duration}h)
                </span>
              </Text>

              {/* Service client name */}
              <Title level={4} className="mt-1 mb-0 text-base md:text-lg">
                {getBookingClientName()}
              </Title>

              {ticket.timeIn && ticket.timeOut && (
                <Text type="secondary" className="text-xs md:text-sm">
                  Confirmed: {formatTime(ticket.timeIn)} -{" "}
                  {formatTime(ticket.timeOut)}
                </Text>
              )}

              {/* Completion Status Indicators */}
              <div className="flex flex-wrap gap-1 md:gap-2 mt-2">
                {getCompletionStatusIndicators(ticket)}
              </div>
            </div>
            <div className="flex-shrink-0">
              <StatusBadge status={ticket.status} />
            </div>
          </div>
        </div>

        <div className="p-3 md:p-4 bg-white border-t rounded-b-lg">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 md:gap-4">
            <div className="space-y-2 md:space-y-3">
              <div>
                <div className="flex items-center space-x-1 text-xs text-gray-500 mb-1">
                  <span>🏢</span>
                  <span>Service Client</span>
                </div>
                <Text strong className="block text-sm md:text-base">
                  {getServiceClientName()}
                </Text>
              </div>
              
              <div>
                <div className="flex items-center space-x-1 text-xs text-gray-500 mb-1">
                  <MapPin size={12} />
                  <span>Location</span>
                </div>
                <Text strong className="block text-sm md:text-base">
                  {ticket.location?.name}
                </Text>
                <Text type="secondary" className="text-xs md:text-sm">
                  {formatFullAddress(ticket.location)}
                </Text>
                <div className="text-xs md:text-sm text-gray-600 mt-1">
                  <span className="font-medium">Contact:</span> {getContactInfo().name}
                  {getContactInfo().phone && ` • ${getContactInfo().phone}`}
                </div>
              </div>
            </div>
            <div className="space-y-2 md:space-y-3">
              <div>
                <div className="flex items-center space-x-1 text-xs text-gray-500 mb-1">
                  <Users2 size={12} />
                  <span>Team</span>
                </div>
                <Text className="text-sm md:text-base">
                  {getTeamMembers()}
                </Text>
              </div>

              {(ticket.scope || ticket.staffNotes) && (
                <div>
                  <div className="flex items-center space-x-1 text-xs text-gray-500 mb-1">
                    <span>📝</span>
                    <span>Notes</span>
                  </div>
                  {ticket.scope && (
                    <div className="text-s md:text-md mb-1">
                      <span className="font-medium">Scope:</span> {ticket.scope}
                    </div>
                  )}
                  {ticket.staffNotes && (
                    <div className="text-xs md:text-sm">
                      <span className="font-medium">Staff Notes:</span> {ticket.staffNotes}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Inventory section with rich details */}
          {ticket.inventories && ticket.inventories.length > 0 && (
            <div className="mt-3 md:mt-4">
              <div className="flex items-center space-x-1 text-xs text-gray-500 mb-2">
                <Package size={12} />
                <span>Inventory ({ticket.inventories.length} items)</span>
              </div>
              <div className="border rounded p-2 md:p-3 bg-gray-50">
                <div className="max-h-32 md:max-h-40 overflow-y-auto">
                  {getDisplayInventories().display
                    .sort((a, b) => {
                      const getNumericValue = (ref: string | undefined) => {
                        if (!ref) return 0;
                        const match = ref.match(/(\d+)/);
                        return match ? parseInt(match[1]) : 0;
                      };
                      return (
                        getNumericValue(a.inventoryRef) -
                        getNumericValue(b.inventoryRef)
                      );
                    })
                    .map((item) => {
                      const formatted = formatInventoryItem(item);
                      return (
                        <div
                          key={item.id}
                          className="py-2 border-b border-gray-200 last:border-b-0"
                        >
                          <div className="flex flex-col md:flex-row md:justify-between gap-1 md:gap-2">
                            <div className="flex-1">
                              <div className="font-medium text-sm">
                                {formatted.refLine}
                              </div>
                              <div className="text-xs text-gray-600">
                                {formatted.titleLine}
                              </div>
                              <div className="flex items-center space-x-3 text-xs text-gray-500 mt-1">
                                <span>{formatted.packingType}</span>
                                <span>{formatted.dimensions}</span>
                                {formatted.value && (
                                  <span className="text-green-600 font-medium">{formatted.value}</span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  {getDisplayInventories().hasMore && (
                    <div className="text-xs text-blue-600 pt-2 border-t mt-2">
                      +{getDisplayInventories().total - 5} more items
                      <Button
                        type="link"
                        size="small"
                        onClick={() => {
                          setDetailsVisible(true);
                          setTicketId(ticket.id);
                        }}
                        className="p-0 h-auto ml-2"
                      >
                        View all
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Supply items */}
          {ticket.supplyItems && ticket.supplyItems.length > 0 && (
            <div className="mt-3 md:mt-4">
              <Text type="secondary" className="text-sm md:text-sm">
                Supply ({ticket.supplyItems.length} items)
              </Text>
              <div className="border rounded p-3 bg-gray-50">
                {ticket.supplyItems.map((item) => (
                  <div
                    key={item?.id}
                    className="flex justify-between text-sm py-1"
                  >
                    <Text>
                      {item.supply?.supplyRef} - {item.supply?.name}
                    </Text>
                    <Text type="secondary">{item.quantity}</Text>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Added services */}
          {ticket.addOnServices && ticket.addOnServices.length > 0 && (
            <div className="mt-3 md:mt-4">
              <div className="flex items-center space-x-1 text-xs text-gray-500 mb-2">
                <Wrench size={12} />
                <span>Added Services</span>
              </div>
              <div className="flex flex-wrap gap-1 md:gap-2">
                {ticket.addOnServices.map((req, index) => (
                  <Tag key={index} className="text-xs md:text-sm">
                    {req.name}
                  </Tag>
                ))}
              </div>
            </div>
          )}

          <div className="mt-4 md:mt-5 pt-3 md:pt-4 border-t flex flex-col md:flex-row justify-end gap-2 md:gap-3">
            <Button
              onClick={() => {
                setDetailsVisible(true);
                setTicketId(ticket.id);
              }}
              className="w-full md:w-auto"
              size="small"
            >
              View Details
            </Button>
            {ticket.status === JobStatus.SCHEDULED && (
              <Button
                type="primary"
                onClick={() => onStartService(ticket.id)}
                loading={updateMutation.isPending}
                className="w-full md:w-auto"
                size="small"
              >
                Start Service
              </Button>
            )}
            {ticket.status === JobStatus.IN_PROGRESS && (
              <>
                <Button
                  onClick={() => onCancelService(ticket.id)}
                  loading={updateMutation.isPending}
                  className="w-full md:w-auto"
                  size="small"
                >
                  Cancel
                </Button>
                <Button
                  type="primary"
                  onClick={() => onCompleteService()}
                  loading={updateMutation.isPending}
                  className="w-full md:w-auto"
                  size="small"
                >
                  Complete Service
                </Button>
              </>
            )}
          </div>
        </div>
      </Card>

      <ServiceDetailsModal
        visible={detailsVisible}
        ticketId={ticketId}
        onClose={() => {
          setDetailsVisible(false);
          setTicketId(null);
        }}
      />
    </>
  );
};